import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import cx from 'classnames';
import { Loading } from 'components';
import VideoPlayerControls, {
  type VideoPlayerControlsConfig,
  type VideoPlayerExtraControls,
  VideoPlayState,
} from './VideoPlayerControls';

import './VideoPlayer.scss';

export type VideoPlayerControlsVariant = 'custom' | 'native' | 'none';

export type VideoPlayerTrack = {
  src: string;
  kind?: string;
  srcLang?: string;
  label?: string;
  default?: boolean;
  ref?: React.MutableRefObject<HTMLTrackElement | null>;
};

export interface VideoPlayerProps {
  src?: string;
  poster?: string;
  preload?: 'auto' | 'metadata' | 'none';
  loop?: boolean;
  autoPlay?: boolean;
  startMuted?: boolean;
  muted?: boolean;
  controlsVariant?: VideoPlayerControlsVariant;
  controlsConfig?: Partial<VideoPlayerControlsConfig>;
  className?: string;
  videoClassName?: string;
  aspectRatio?: string;
  isDisabled?: boolean;
  isLive?: boolean;
  hideDuration?: boolean;
  enableKeyboardShortcuts?: boolean;
  enableFrameStepShortcuts?: boolean;
  extraControls?: VideoPlayerExtraControls;
  tracks?: VideoPlayerTrack[];
  overlay?: React.ReactNode;
  loading?: boolean;
  error?: boolean;
  errorContent?: React.ReactNode;
  crossOrigin?: 'anonymous' | 'use-credentials';
  testId?: string;
  playsInline?: boolean;
  onPlay?: () => void;
  onPause?: () => void;
  onEnded?: () => void;
  onError?: (event: React.SyntheticEvent<HTMLVideoElement, Event>) => void;
  onTimeUpdate?: (time: number) => void;
  onLoadedMetadata?: (duration: number) => void;
  onDurationChange?: (duration: number) => void;
  videoRef?: React.MutableRefObject<HTMLVideoElement | null>;
}

const defaultControlsConfig: VideoPlayerControlsConfig = {
  showPlayPause: true,
  showTimeline: true,
  showDuration: true,
  showPlaybackRate: true,
  showMute: true,
};

const VideoPlayer = forwardRef<HTMLVideoElement, VideoPlayerProps>(
  (
    {
      src,
      poster,
      preload = 'metadata',
      loop,
      autoPlay,
      startMuted,
      muted,
      controlsVariant = 'custom',
      controlsConfig,
      className,
      videoClassName,
      aspectRatio,
      isDisabled = false,
      isLive = false,
      hideDuration = false,
      enableKeyboardShortcuts = false,
      enableFrameStepShortcuts = false,
      extraControls,
      tracks,
      overlay,
      loading,
      error,
      errorContent,
      crossOrigin,
      testId,
      playsInline = true,
      onPlay,
      onPause,
      onEnded,
      onError,
      onTimeUpdate,
      onLoadedMetadata,
      onDurationChange,
      videoRef: providedVideoRef,
    },
    forwardedRef
  ) => {
    const internalVideoRef = useRef<HTMLVideoElement | null>(null);
    const videoRef = providedVideoRef ?? internalVideoRef;

    useImperativeHandle(
      forwardedRef,
      () => videoRef.current as HTMLVideoElement
    );

    const mergedControlsConfig = useMemo(
      () => ({ ...defaultControlsConfig, ...controlsConfig }),
      [controlsConfig]
    );

    const { aspectRatioCss, aspectRatioValue } = useMemo(() => {
      if (!aspectRatio) {
        return { aspectRatioCss: undefined, aspectRatioValue: undefined };
      }

      const cleaned = aspectRatio.replace(/\s+/g, '');
      const parts = cleaned.includes('/')
        ? cleaned.split('/')
        : cleaned.split(':');

      if (parts.length === 2) {
        const [widthRaw, heightRaw] = parts;
        const width = Number(widthRaw);
        const height = Number(heightRaw);
        const hasNumericRatio =
          width > 0 &&
          height > 0 &&
          Number.isFinite(width) &&
          Number.isFinite(height);

        return {
          aspectRatioCss: `${widthRaw} / ${heightRaw}`,
          aspectRatioValue: hasNumericRatio ? width / height : undefined,
        };
      }

      return { aspectRatioCss: aspectRatio, aspectRatioValue: undefined };
    }, [aspectRatio]);

    const [playState, setPlayState] = useState<VideoPlayState>(
      VideoPlayState.initial
    );
    const [duration, setDuration] = useState<number>(0);
    const [currentTime, setCurrentTime] = useState<number>(0);
    const [isMuted, setIsMuted] = useState<boolean>(!!startMuted || !!muted);
    const [isVideoReady, setIsVideoReady] = useState<boolean>(false);

    useEffect(() => {
      if (typeof muted === 'boolean') {
        setIsMuted(muted);
      }
    }, [muted]);

    useEffect(() => {
      if (videoRef.current) {
        videoRef.current.muted = isMuted;
      }
    }, [isMuted, videoRef]);

    useEffect(() => {
      setCurrentTime(0);
      setDuration(0);
      setPlayState(VideoPlayState.initial);
      setIsVideoReady(false);
    }, [src]);

    const handleLoadedMetadata = useCallback(
      (event: React.SyntheticEvent<HTMLVideoElement, Event>) => {
        const media = event.currentTarget;
        const mediaDuration = Number.isFinite(media.duration)
          ? media.duration
          : 0;

        setDuration(mediaDuration);
        setIsVideoReady(true);
        onLoadedMetadata?.(mediaDuration);
      },
      [onLoadedMetadata]
    );

    const handleDurationChange = useCallback(
      (event: React.SyntheticEvent<HTMLVideoElement, Event>) => {
        const media = event.currentTarget;
        const mediaDuration = Number.isFinite(media.duration)
          ? media.duration
          : 0;

        setDuration(mediaDuration);
        onDurationChange?.(mediaDuration);
      },
      [onDurationChange]
    );

    const handleTimeUpdate = useCallback(
      (event: React.SyntheticEvent<HTMLVideoElement, Event>) => {
        const media = event.currentTarget;
        setCurrentTime(media.currentTime);
        onTimeUpdate?.(media.currentTime);
      },
      [onTimeUpdate]
    );

    const handlePlay = useCallback(() => {
      if (isDisabled) return;
      const media = videoRef.current;
      media
        ?.play()
        .then(() => {
          setPlayState(VideoPlayState.playing);
          onPlay?.();
        })
        .catch(() => {
          setPlayState(VideoPlayState.paused);
        });
    }, [isDisabled, onPlay, videoRef]);

    const handlePause = useCallback(() => {
      const media = videoRef.current;
      media?.pause();
      setPlayState(VideoPlayState.paused);
      onPause?.();
    }, [onPause, videoRef]);

    const handleReplay = useCallback(() => {
      const media = videoRef.current;
      if (!media) return;

      media.currentTime = 0;
      handlePlay();
      setPlayState(VideoPlayState.playing);
    }, [handlePlay, videoRef]);

    const handleEnded = useCallback(() => {
      setPlayState(VideoPlayState.ended);
      onEnded?.();
    }, [onEnded]);

    const handleError = useCallback(
      (event: React.SyntheticEvent<HTMLVideoElement, Event>) => {
        setPlayState(VideoPlayState.paused);
        onError?.(event);
      },
      [onError]
    );

    const handleMute = useCallback(() => setIsMuted(true), []);
    const handleUnmute = useCallback(() => setIsMuted(false), []);

    const setTimestamp = useCallback(
      (time: number) => {
        const media = videoRef.current;
        if (!media) return;

        media.currentTime = time;
        setCurrentTime(time);
      },
      [videoRef]
    );

    const showCustomControls = controlsVariant === 'custom';
    const showNativeControls = controlsVariant === 'native';
    const rootClassName = cx('video-player', className, {
      'video-player--with-aspect-ratio': !!aspectRatioCss,
    });
    const rootStyle = aspectRatioValue
      ? { ['--video-player-ratio' as string]: aspectRatioValue }
      : undefined;
    const videoClassNames = cx('video-player__video', videoClassName, {
      'video-player__video--with-aspect-ratio': !!aspectRatioCss,
    });

    const mediaWrapperStyle = aspectRatioCss
      ? {
          aspectRatio: aspectRatioCss,
        }
      : undefined;
    const mediaClassNames = cx('video-player__media', {
      'video-player__media--with-aspect-ratio': !!aspectRatioCss,
    });
    const isLoading = loading || (src && !isVideoReady && !error);

    return (
      <div className={rootClassName} style={rootStyle} data-testid={testId}>
        <div className={mediaClassNames} style={mediaWrapperStyle}>
          <div className="video-player__frame">
            {overlay && (
              <div className="video-player__custom-overlay">{overlay}</div>
            )}
            {isLoading && (
              <div className="video-player__overlay video-player__overlay--loading">
                <Loading visible />
              </div>
            )}
            {error && errorContent && (
              <div className="video-player__overlay video-player__overlay--error">
                {errorContent}
              </div>
            )}
            <video
              ref={videoRef}
              className={videoClassNames}
              poster={poster}
              preload={preload}
              loop={loop}
              controls={showNativeControls}
              playsInline={playsInline}
              muted={isMuted}
              autoPlay={autoPlay}
              crossOrigin={crossOrigin}
              onLoadedMetadata={handleLoadedMetadata}
              onDurationChange={handleDurationChange}
              onTimeUpdate={handleTimeUpdate}
              onPlay={() => setPlayState(VideoPlayState.playing)}
              onPause={() => setPlayState(VideoPlayState.paused)}
              onEnded={handleEnded}
              onError={handleError}
            >
              {src && <source src={src} />}
              {tracks?.map(track => (
                <track
                  key={`${track.src}-${track.label ?? track.srcLang ?? ''}`}
                  ref={track.ref}
                  src={track.src}
                  kind={track.kind}
                  srcLang={track.srcLang}
                  label={track.label}
                  default={track.default}
                />
              ))}
            </video>
          </div>
        </div>

        {showCustomControls && (
          <VideoPlayerControls
            playState={playState}
            isDisabled={isDisabled || !!loading || !!error}
            isLive={isLive}
            duration={duration}
            currentTime={currentTime}
            setCurrentTime={setTimestamp}
            onPlay={handlePlay}
            onPause={handlePause}
            onReplay={handleReplay}
            isMuted={isMuted}
            mute={handleMute}
            unmute={handleUnmute}
            videoRef={videoRef}
            controlsConfig={{
              ...mergedControlsConfig,
              showDuration: mergedControlsConfig.showDuration && !hideDuration,
            }}
            enableKeyboardShortcuts={enableKeyboardShortcuts}
            enableFrameStepShortcuts={enableFrameStepShortcuts}
            extraControls={extraControls}
          />
        )}
      </div>
    );
  }
);

VideoPlayer.displayName = 'VideoPlayer';

export default VideoPlayer;
